import { forwardRef, useImperativeHandle, useState } from 'react'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import {
  Alignment,
  Base64UploadAdapter,
  Bold,
  ClassicEditor,
  Essentials,
  FileRepository,
  Font,
  FontColor,
  Heading,
  Image,
  ImageCaption,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  MediaEmbed,
  Paragraph,
  RemoveFormat
} from 'ckeditor5'
import 'ckeditor5/ckeditor5-editor.css'
import 'ckeditor5/ckeditor5-content.css'
import './ck-content-fixes.css'

export const Editor = forwardRef(({ content, onContentChange }, ref) => {
  const [editorInstance, setEditorInstance] = useState(null)
  const editorConfiguration = {
    licenseKey: 'GPL',
    plugins: [
      Essentials,
      Paragraph,
      Heading,
      List,
      Bold,
      Italic,
      Link,
      Image,
      ImageCaption,
      ImageStyle,
      ImageToolbar,
      ImageUpload,
      ImageResize,
      ImageInsert,
      ImageTextAlternative,
      FileRepository,
      Base64UploadAdapter,
      Font,
      Alignment,
      FontColor,
      MediaEmbed,
      RemoveFormat,
      Indent,
      IndentBlock
    ],
    toolbar: {
      items: [
        'undo',
        'redo',
        '|',
        'heading',
        '|',
        'bold',
        'italic',
        'fontSize',
        'fontColor',
        '|',
        'alignment',
        '|',
        'bulletedList',
        'numberedList',
        'outdent',
        'indent',
        '|',
        'link',
        'insertImage',
        'mediaEmbed',
        '|',
        'removeFormat'
      ]
    },
    heading: {
      options: [
        { model: 'paragraph', title: 'Paragraph' },
        { model: 'heading2', view: 'h2', title: 'Heading 2' },
        { model: 'heading3', view: 'h3', title: 'Heading 3' }
      ]
    },
    image: {
      toolbar: [
        'imageStyle:inline',
        'imageStyle:block',
        'imageStyle:side',
        '|',
        'toggleImageCaption',
        'imageTextAlternative',
        '|',
        'imageResize'
      ],
      styles: ['alignLeft', 'alignCenter', 'alignRight'],
      resizeOptions: [
        {
          name: 'imageResize:original',
          label: 'Original',
          value: null
        },
        {
          name: 'imageResize:50',
          label: '50%',
          value: '50'
        },
        {
          name: 'imageResize:75',
          label: '75%',
          value: '75'
        }
      ]
    },
    mediaEmbed: {
      previewsInData: true
    }
  }

  const handleReady = (editor) => {
    setEditorInstance(editor)
    editor.editing.view.change((writer) => {
      writer.setAttribute(
        'aria-label',
        'Story Body Content Editor',
        editor.editing.view.document.getRoot()
      )
    })

    // Add event listener to encourage alt text for better accessibility
    if (editor.model && editor.model.document) {
      editor.model.document.on('change', () => {
        const changes = editor.model.document.differ.getChanges()

        changes.forEach((change) => {
          if (change.type === 'insert' && change.name === 'imageBlock') {
            const imageElement = change.position.nodeAfter
            if (imageElement && imageElement.name === 'imageBlock') {
              // Check if alt attribute is empty after a short delay to allow for user input
              setTimeout(() => {
                const altText = imageElement.getAttribute('alt')
                if (!altText || altText.trim() === '') {
                  // Focus the image to trigger the alt text dialog
                  editor.model.change((writer) => {
                    writer.setSelection(imageElement, 'on')
                  })

                  // Show a gentle reminder about alt text
                  console.warn(
                    'Reminder: Please add alt text to your image for better accessibility. Click the image and use the "Alt text" button in the toolbar.'
                  )
                }
              }, 100)
            }
          }
        })
      })
    }
  }

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (editorInstance) {
        editorInstance.editing.view.focus()
      }
    }
  }))

  const handleChange = (event, editor) => {
    onContentChange(editor.getData())
  }

  return (
    <CKEditor
      editor={ClassicEditor}
      config={editorConfiguration}
      onReady={handleReady}
      data={content}
      onChange={handleChange}
    />
  )
})
